"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type BacklogOriginDrawerPayload = {
  originId: string;
  originContent: string;
  language?: string | null;
  onExtractionSuccess?: () => void;
};

interface BacklogOriginDrawersContextValue {
  extractThoughtsDrawer: BacklogOriginDrawerPayload | null;
  identifyTopicsDrawer: BacklogOriginDrawerPayload | null;
  openExtractThoughtsDrawer: (payload: BacklogOriginDrawerPayload) => void;
  closeExtractThoughtsDrawer: () => void;
  openIdentifyTopicsDrawer: (payload: BacklogOriginDrawerPayload) => void;
  closeIdentifyTopicsDrawer: () => void;
}

const BacklogOriginDrawersContext = createContext<
  BacklogOriginDrawersContextValue | undefined
>(undefined);

export function BacklogOriginDrawersProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [extractThoughtsDrawer, setExtractThoughtsDrawer] =
    useState<BacklogOriginDrawerPayload | null>(null);
  const [identifyTopicsDrawer, setIdentifyTopicsDrawer] =
    useState<BacklogOriginDrawerPayload | null>(null);

  const openExtractThoughtsDrawer = useCallback(
    (payload: BacklogOriginDrawerPayload) => {
      setExtractThoughtsDrawer(payload);
    },
    [],
  );

  const closeExtractThoughtsDrawer = useCallback(() => {
    setExtractThoughtsDrawer(null);
  }, []);

  const openIdentifyTopicsDrawer = useCallback(
    (payload: BacklogOriginDrawerPayload) => {
      setIdentifyTopicsDrawer(payload);
    },
    [],
  );

  const closeIdentifyTopicsDrawer = useCallback(() => {
    setIdentifyTopicsDrawer(null);
  }, []);

  const contextValue = useMemo(
    () => ({
      extractThoughtsDrawer,
      identifyTopicsDrawer,
      openExtractThoughtsDrawer,
      closeExtractThoughtsDrawer,
      openIdentifyTopicsDrawer,
      closeIdentifyTopicsDrawer,
    }),
    [
      extractThoughtsDrawer,
      identifyTopicsDrawer,
      openExtractThoughtsDrawer,
      closeExtractThoughtsDrawer,
      openIdentifyTopicsDrawer,
      closeIdentifyTopicsDrawer,
    ],
  );

  return (
    <BacklogOriginDrawersContext.Provider value={contextValue}>
      {children}
    </BacklogOriginDrawersContext.Provider>
  );
}

export function useBacklogOriginDrawers() {
  const context = useContext(BacklogOriginDrawersContext);

  if (!context) {
    throw new Error(
      "useBacklogOriginDrawers must be used within BacklogOriginDrawersProvider",
    );
  }

  return context;
}
